/** Hva laksen hører, som en vekting.
 *
 *  VENDET KOPI. Kilden er `analyzer/domain/audiogram.py` i phoniq-analyzer, og
 *  tallene under er de samme. Grunnen til at den ligger her framfor å hentes
 *  over API-et er den samme som `dsp.ts` har i labeler: et bygg her skal ikke
 *  avhenge av at et søskenoppsett finnes og står på riktig revisjon. Endres
 *  tabellen der, skal den endres her -- den er ni tallpar og en konstant.
 *
 *  KILDE
 *      Hawkins, A. D. & Johnstone, A. D. F. (1978). «The hearing of the
 *      Atlantic salmon, Salmo salar.» Journal of Fish Biology 13(6): 655-673.
 *
 *  FORBEHOLDET SOM STYRER ALL BRUK
 *      Laksefisk har ingen gassforbindelse mellom svømmeblære og indre øre. De
 *      oppfatter PARTIKKELBEVEGELSE, ikke trykk. De absolutte dB-verdiene er
 *      derfor ikke et frifelt-audiogram og kan aldri brukes til å hevde at en
 *      lyd var eller ikke var hørbar. Det som overlever forbeholdet er FORMEN:
 *      følsomheten topper rundt 100-160 Hz og faller bratt til begge sider. Form
 *      er alt en relativ vekting trenger, og relativt er alt dette brukes til.
 */

/** (frekvens Hz, terskel dB re 1 µPa). Formen, ikke sertifiserte absoluttall. */
const TERSKLER: [number, number][] = [
  [32, 95], [64, 90], [100, 89], [128, 89], [160, 92],
  [200, 96], [250, 100], [320, 108], [380, 115],
]

/** Utenfor dette spennet finnes ingen målt deteksjon, så et bånd der bidrar
 *  ingenting framfor å bidra med en ekstrapolert gjetning. Den øvre grensen er
 *  den viktigste: laksen hører ikke regnet og fôrsystemet på 5-10 kHz som
 *  dominerer dette arkivet i nivå. */
export const HOERER_LO_HZ = 30
export const HOERER_HI_HZ = 400

/** Mer enn dette under beste følsomhet regnes som uhørbart. Uten et gulv ville
 *  de bratte skråningene latt en svært høy lyd i et knapt hørbart bånd
 *  dominere summen gjennom rent nivå -- nøyaktig den artefakten vekting finnes
 *  for å hindre. */
const MAKS_DEMPING_DB = 25

const BESTE_TERSKEL_DB = Math.min(...TERSKLER.map(([, t]) => t))

/** Relativ følsomhet i dB ved én frekvens. 0 dB på toppen, −Infinity utenfor.
 *
 *  Interpolert i log-frekvens, som er slik hørselskurver tegnes og slik
 *  båndaksen er spredd; lineær interpolasjon over en dekade ville lagt toppen
 *  på feil sted. */
export function vektDb(f: number): number {
  if (!(f >= HOERER_LO_HZ) || f > HOERER_HI_HZ) return -Infinity
  const x = Math.log10(f)
  let terskel = TERSKLER[0]![1]
  if (x <= Math.log10(TERSKLER[0]![0])) terskel = TERSKLER[0]![1]
  else if (x >= Math.log10(TERSKLER[TERSKLER.length - 1]![0])) {
    terskel = TERSKLER[TERSKLER.length - 1]![1]
  } else {
    for (let i = 1; i < TERSKLER.length; i++) {
      const [f1, t1] = TERSKLER[i]!
      if (x <= Math.log10(f1)) {
        const [f0, t0] = TERSKLER[i - 1]!
        const a = (x - Math.log10(f0)) / (Math.log10(f1) - Math.log10(f0))
        terskel = t0 + a * (t1 - t0)
        break
      }
    }
  }
  return Math.max(-(terskel - BESTE_TERSKEL_DB), -MAKS_DEMPING_DB)
}

/** Hvor mye over det vanlige timen lå, i den valutaen fisken hører i.
 *
 *  Et vektet MIDDEL av avviket per bånd, ikke en nivådifferanse. Forskjellen
 *  er verdt å være presis om: en effektsum ville latt det ene høyeste båndet
 *  bestemme svaret, og spørsmålet her er ikke «hvor mye energi» men «hvor mye
 *  av det den hører var uvanlig». Vektene er audiogrammets, i effektdomenet, så
 *  et bånd på kanten av hørselen teller mindre enn ett midt i den.
 *
 *  Null bånd innenfor hørselen gir null -- ikke en oppdiktet verdi. */
export function hoertAvvikDb(
  senterHz: (number | null)[], avvikDb: (number | null)[],
): number | null {
  let sumVekt = 0
  let sum = 0
  for (let i = 0; i < senterHz.length; i++) {
    const f = senterHz[i]
    const d = avvikDb[i]
    if (f == null || d == null || !Number.isFinite(d)) continue
    const w = vektDb(f)
    if (!Number.isFinite(w)) continue
    const lin = Math.pow(10, w / 10)
    sumVekt += lin
    sum += lin * d
  }
  return sumVekt > 0 ? sum / sumVekt : null
}

/** De to tersklene hvilescoren selv bruker, så blobben og scoren aldri sier
 *  hver sin ting om samme time. Speiler `DISTURBED_EXCESS_DB` og
 *  `STRONG_EXCESS_DB` i `analyzer/domain/rest.py`. */
export const FORSTYRRET_DB = 6
export const STERK_DB = 12
